// lib/organisation-teams/team-rules.ts
//
// The single source of truth for what the organisation-team surfaces are allowed to do with
// credits and members. Both team UIs (the admin section in OrganisationMenu and the budget-pool
// canvas of the Organisation page) computed these rules separately and already disagreed.
//
// Client-side module on purpose: it must stay importable from components, so it never imports
// Prisma, `#lib/db`, or anything that reaches them. Only erased `import type` is allowed here.
import type {
  OrganisationTeamDTO,
  TeamBudgetDeltaDirection,
  TeamBudgetDeltaRefusal,
  TeamCreditPool,
} from '#types/types';

/** Team plan shape the credit rules need. Structural on purpose: DTOs, detail payloads and canvas nodes all satisfy it. */
interface TeamPlanCredits {
  reservedCreditsFloat?: number | null;
  usedCreditsFloat?: number | null;
  sourceOrganisationPlanId?: string | null;
}

interface TeamWithPlan {
  teamPlan?: TeamPlanCredits | null;
}

/** Organisation plan shape as the inventory DTO exposes it. */
interface OrganisationPlanCredits {
  includedCredits?: number | null;
  usedCredits?: number | null;
  reservedCredits?: number | null;
}

/** Identity of one end of a team-to-team credit transfer. */
interface TeamTransferRef {
  teamId: string;
  sourcePlanId: string | null;
}

const toFinite = (value: number | null | undefined): number =>
  (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const isPositiveAmount = (amount: number): boolean => Number.isFinite(amount) && amount > 0;

/** Credit position of a team: what it holds, what it burnt, what is still spendable. */
export const readTeamCreditPool = (team: TeamWithPlan | null | undefined): TeamCreditPool => {
  const reserved = toFinite(team?.teamPlan?.reservedCreditsFloat);
  const used = toFinite(team?.teamPlan?.usedCreditsFloat);
  return {
    reserved,
    used,
    remaining: Math.max(reserved - used, 0),
    sourcePlanId: team?.teamPlan?.sourceOrganisationPlanId ?? null,
  };
};

export const getTeamRemainingCredits = (team: TeamWithPlan | null | undefined): number =>
  readTeamCreditPool(team).remaining;

/** Credits of an organisation plan that no team reserved and nobody spent yet. */
export const getOrganisationPlanAvailableCredits = (plan: OrganisationPlanCredits | null | undefined): number =>
  Math.max(toFinite(plan?.includedCredits) - toFinite(plan?.usedCredits) - toFinite(plan?.reservedCredits), 0);

/** A team can only send credits to another team funded by the very same organisation plan. */
export const isEligibleTransferTarget = (from: TeamTransferRef, to: TeamTransferRef | null | undefined): boolean =>
  Boolean(from.sourcePlanId) && Boolean(to) && to!.teamId !== from.teamId && to!.sourcePlanId === from.sourcePlanId;

export const canTransferCredits = (input: {
  fromTeamId: string;
  fromPool: TeamCreditPool;
  to: TeamTransferRef | null;
  amount: number;
}): boolean =>
  isEligibleTransferTarget({ teamId: input.fromTeamId, sourcePlanId: input.fromPool.sourcePlanId }, input.to)
  && isPositiveAmount(input.amount)
  && input.amount <= input.fromPool.remaining;

/**
 * `allowSourceTopUp` mirrors the Fater-admin-only auto top-up: the source plan is grown to cover
 * the request, so its current free balance stops being a ceiling.
 */
export const canCreateTeam = (input: {
  name: string;
  sourcePlanId: string;
  credits: number;
  sourcePlanAvailable: number;
  allowSourceTopUp?: boolean;
}): boolean =>
  Boolean(input.name.trim())
  && Boolean(input.sourcePlanId)
  && isPositiveAmount(input.credits)
  && (input.allowSourceTopUp === true || input.credits <= input.sourcePlanAvailable);

/** Ceiling of an absolute reservation: the plan's free credits, plus what this team already holds on that same plan. */
export const getMaxReservableCredits = (input: {
  sourcePlanAvailable: number;
  currentReserved: number;
  isSameSourcePlan: boolean;
}): number => input.sourcePlanAvailable + (input.isSameSourcePlan ? input.currentReserved : 0);

export const getRequiredSourceTopUp = (requestedCredits: number, maxReservable: number): number =>
  Math.max(toFinite(requestedCredits) - maxReservable, 0);

/** Set an absolute team reservation (the budget dialog), as opposed to the canvas' relative delta. */
export const canSetTeamReservedCredits = (input: {
  requestedCredits: number;
  pool: TeamCreditPool;
  maxReservable: number;
  allowSourceTopUp?: boolean;
}): boolean =>
  isPositiveAmount(input.requestedCredits)
  && input.requestedCredits >= input.pool.used
  && (input.allowSourceTopUp === true || input.requestedCredits <= input.maxReservable);

/** Where a relative budget move lands the team reservation. */
export const getNextReservedCredits = (
  pool: TeamCreditPool,
  direction: TeamBudgetDeltaDirection,
  amount: number,
): number => (direction === 'ALLOCATE_FROM_ORG' ? pool.reserved + amount : pool.reserved - amount);

/**
 * Why a relative budget move is refused, or `null` when it is allowed. The canvas needs the reason
 * to say something true to the user; `canApplyTeamBudgetDelta` is the same rule as a boolean.
 *
 * A delta is only an absolute reservation expressed relatively, so it is judged by the very
 * function the budget dialog uses: a team keeps a strictly positive reservation and never sits
 * below what it already spent, in BOTH directions. On the allocation side the source-plan ceiling
 * `amount <= sourcePlanAvailable` is exactly `nextReserved <= sourcePlanAvailable + reserved`,
 * which is what `getMaxReservableCredits` computes for the team's own plan; a release gives credits
 * back, so nothing is drawn from the plan and its free balance plays no part.
 */
export const checkTeamBudgetDelta = (input: {
  pool: TeamCreditPool;
  direction: TeamBudgetDeltaDirection;
  amount: number;
  /** Free credits on the team's source plan. Ignored for a release. */
  sourcePlanAvailable?: number;
}): TeamBudgetDeltaRefusal | null => {
  if (!input.pool.sourcePlanId || !isPositiveAmount(input.amount)) return 'INVALID_AMOUNT';
  const maxReservable = getMaxReservableCredits({
    sourcePlanAvailable: input.direction === 'ALLOCATE_FROM_ORG' ? (input.sourcePlanAvailable ?? 0) : 0,
    currentReserved: input.pool.reserved,
    isSameSourcePlan: true,
  });
  const nextReserved = getNextReservedCredits(input.pool, input.direction, input.amount);
  if (canSetTeamReservedCredits({ requestedCredits: nextReserved, pool: input.pool, maxReservable })) return null;
  return nextReserved > maxReservable ? 'SOURCE_PLAN_EXHAUSTED' : 'RESERVATION_BELOW_USED';
};

export const canApplyTeamBudgetDelta = (input: Parameters<typeof checkTeamBudgetDelta>[0]): boolean =>
  checkTeamBudgetDelta(input) === null;

/** Move credits from the team's source plan into the team reservation. */
export const canAllocateFromOrg = (input: {
  pool: TeamCreditPool;
  amount: number;
  sourcePlanAvailable: number;
}): boolean => canApplyTeamBudgetDelta({ ...input, direction: 'ALLOCATE_FROM_ORG' });

/** Give credits back to the source plan. */
export const canReleaseToOrg = (input: { pool: TeamCreditPool; amount: number }): boolean =>
  canApplyTeamBudgetDelta({ ...input, direction: 'RELEASE_TO_ORG' });

/** Members belonging to no team: the organisation pool, and the pick list when adding members to a team. */
export const getUnassignedMembers = <T extends { id: string; teamId?: string | null }>(
  members: T[],
  teams: { memberIds: string[] }[],
): T[] => {
  const assignedIds = new Set(teams.flatMap((team) => team.memberIds));
  return members.filter((member) => !member.teamId && !assignedIds.has(member.id));
};

/**
 * The Team inventory registry and the Organisation detail snapshot both carry teams. The snapshot
 * wins on shared fields (it is the freshest write), the registry keeps the `stats` it alone loads.
 */
export const mergeOrganisationTeams = (
  inventoryTeams: OrganisationTeamDTO[],
  snapshotTeams: OrganisationTeamDTO[] | null | undefined,
): OrganisationTeamDTO[] => {
  const byId = new Map<string, OrganisationTeamDTO>();
  inventoryTeams.forEach((team) => byId.set(team.id, team));
  (snapshotTeams ?? []).forEach((team) => {
    const existing = byId.get(team.id);
    byId.set(team.id, {
      ...(existing ?? {}),
      ...team,
      ...(existing?.stats ? { stats: existing.stats } : {}),
    });
  });
  return Array.from(byId.values()).sort((left, right) => left.createdAt.localeCompare(right.createdAt));
};
